/**
 * Below are the colors that are used in the app. The colors are defined in the light and dark mode.
 * There are many other ways to style your app. For example, [Nativewind](https://www.nativewind.dev/), [Tamagui](https://tamagui.dev/), [unistyles](https://reactnativeunistyles.vercel.app), etc.
 */

const tintColorLight = '#0a7ea4';
const tintColorDark = '#fff';

export const Colors = {
  light: {
    text: '#11181C',
    background: '#fff',
    tint: tintColorLight,
    icon: '#687076',
    tabIconDefault: '#687076',
    tabIconSelected: tintColorLight,
    // Campaign screen specific colors
    cardBackground: '#f8f9fa',
    border: '#e9ecef',
    secondaryText: '#6c757d',
    tertiaryText: '#868e96',
    labelText: '#495057',
    warningText: '#b00020',
    progressBackground: '#e9ecef',
    // Share screen specific colors
    formBackground: '#fff',
    inputBackground: '#fff',
    inputBorder: '#ccc',
    errorText: '#e74c3c',
    successText: '#4CAF50',
    // Status background colors
    visitedBackground: '#f0f9f0',
    notInBackground: '#fdf2f2',
    // Progress bar colors
    progressVisited: '#6c757d',
    progressNotIn: '#adb5bd',
    progressContacted: '#ced4da',
    // Progress badge colors
    progressHigh: '#4CAF50',
    progressMedium: '#FF9800',
    progressLow: '#9E9E9E',
    progressAssigned: '#F44336',
  },
  dark: {
    text: '#ECEDEE',
    background: '#151718',
    tint: tintColorDark,
    icon: '#9BA1A6',
    tabIconDefault: '#9BA1A6',
    tabIconSelected: tintColorDark,
    // Campaign screen specific colors
    cardBackground: '#2c2c2e',
    border: '#3a3a3c',
    secondaryText: '#8e8e93',
    tertiaryText: '#8e8e93',
    labelText: '#8e8e93',
    warningText: '#ff6b6b',
    progressBackground: '#3a3a3c',
    // Share screen specific colors
    formBackground: '#1c1c1e',
    inputBackground: '#2c2c2e',
    inputBorder: '#3a3a3c',
    errorText: '#ff6b6b',
    successText: '#32d74b',
    // Status background colors
    visitedBackground: '#263a26',
    notInBackground: '#3a2626',
    // Progress bar colors
    progressVisited: '#B0B3B8',
    progressNotIn: '#8e8e93',
    progressContacted: '#636366',
    // Progress badge colors
    progressHigh: '#32d74b',
    progressMedium: '#ff9f0a',
    progressLow: '#8e8e93',
    progressAssigned: '#ff453a',
  },
};

/**
 * Party Colors
 * Centralized color definitions for political parties used across the application
 * These colors are used in pie charts, VI dialogs, and other UI components
 */
export const PARTY_COLORS = {
  // Reform Party
  'R': '#12B6CF', // Reform - Blue
  'Z': '#87CEEB', // Possible Reform - Light Blue
  
  // Against Reform
  'A': '#8B5CF6', // Against Reform - Purple
  
  // Conservative Party
  'C': '#0087DC', // Conservative - Blue
  
  // Green Party
  'G': '#528D6B', // Green - Green
  
  // Independent
  'I': '#808080', // Independent - Grey
  
  // Liberal Democrats
  'L': '#FAA61A', // Liberal Democrat - Orange
  
  // Nationalist
  'N': '#0066CC', // Nationalist - Dark Blue
  
  // Labour Party
  'S': '#E4003B', // Socialist (Labour) - Red
  
  // Special cases
  'U': '#6B7280', // Undecided - Light Grey
  'W': '#374151', // Will not vote - Dark Grey
} as const;

/**
 * Get party color by code
 * @param code - Party code (R, Z, A, C, G, I, L, N, S, U, W)
 * @returns Color hex string or undefined if not found
 */
export function getPartyColor(code: string | null): string | undefined {
  if (!code) return undefined;
  return PARTY_COLORS[code as keyof typeof PARTY_COLORS];
}

/**
 * Get all party colors as an array
 * @returns Array of color objects with code and color
 */
export function getAllPartyColors(): Array<{ code: string; color: string }> {
  return Object.entries(PARTY_COLORS).map(([code, color]) => ({ code, color }));
}

/**
 * Check if a color is light (for accessibility purposes)
 * @param color - Hex color string
 * @returns true if color is light, false if dark
 */
export function isLightColor(color: string): boolean {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5;
}

/**
 * Get border color for light colors (for accessibility)
 * @param color - Hex color string
 * @returns Border color for light colors, undefined for dark colors
 */
export function getBorderColorForLightColor(color: string): string | undefined {
  return isLightColor(color) ? '#E5E7EB' : undefined;
}